import { useState } from "react";
import { groupByCategory, type WhatsNewCategory, type WhatsNewRelease } from "@keydials/ui";
import { Modal } from "./Modal";
import { Icon, type IconName } from "../Icon";

const CATEGORY_META: Record<WhatsNewCategory, { icon: IconName; colour: string; label: string }> = {
  New: { icon: "sparkles", colour: "#3eb034", label: "New" },
  Improved: { icon: "activity", colour: "#3b82f6", label: "Improved" },
  Fixed: { icon: "check", colour: "#d97706", label: "Fixed" },
};

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

/** Platform admins only: whether a release is shown to everyone. An "Admin only" chip marks the ones customers
 * can't see yet, and ticking Public shows it to them in whichever apps it targets. */
function PublicToggle({ isPublic, onChange }: { isPublic: boolean; onChange: (isPublic: boolean) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function change(next: boolean) {
    setBusy(true);
    setFailed(false);
    try {
      await onChange(next);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
      {!isPublic && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 99,
            background: "var(--surface-2)",
            color: "var(--text-muted)",
          }}
        >
          <Icon name="lock" size={11} /> Admin only
        </span>
      )}
      <label
        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-muted)", cursor: busy ? "default" : "pointer" }}
        title="Public updates are shown to everyone. The rest only platform admins see."
      >
        <input type="checkbox" checked={isPublic} disabled={busy} onChange={(e) => void change(e.target.checked)} />
        Public
      </label>
      {failed && <span style={{ fontSize: 12, color: "var(--st-overdue)" }}>Didn&rsquo;t save</span>}
    </span>
  );
}

/** One release: its title and date, then its items grouped under New / Improved / Fixed. Shared by the unread view
 * and the full history (KEY-606) so the two read identically. */
function ReleaseSection({
  release,
  seen,
  onSetPublic,
}: {
  release: WhatsNewRelease;
  seen?: boolean;
  /** Present for platform admins only. */
  onSetPublic?: (key: string, isPublic: boolean) => Promise<void>;
}) {
  return (
    <section>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: seen ? "var(--text-muted)" : "var(--text)" }}>{release.title}</h3>
        <span style={{ fontSize: 12, color: "var(--text-faint)", whiteSpace: "nowrap" }}>{formatDate(release.publishedOn)}</span>
      </div>
      {onSetPublic && <PublicToggle isPublic={release.isPublic ?? false} onChange={(next) => onSetPublic(release.key, next)} />}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {groupByCategory(release).map(({ category, items }) => {
          const meta = CATEGORY_META[category];
          return (
            <div key={category} style={{ display: "flex", gap: 11 }}>
              <span
                style={{
                  flex: "none",
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `${meta.colour}1f`,
                  color: meta.colour,
                }}
              >
                <Icon name={meta.icon} size={15} />
              </span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.2, color: meta.colour, marginBottom: 3 }}>
                  {meta.label}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
                  {items.map((item, i) => (
                    <li key={i} style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
                      {item.description}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** The auto-opening "What's New" dialog for the user app. Presentation only — the data + open/close is owned
 * by the shared `useWhatsNew` hook in the header. */
export function WhatsNewModal({
  releases,
  onClose,
  loadAll,
  onSetPublic,
}: {
  releases: WhatsNewRelease[];
  onClose: (dontShowAgain: boolean) => void;
  /** Platform admins only: make a release public or admin-only. Updates the unread list itself. */
  onSetPublic?: (key: string, isPublic: boolean) => Promise<void>;
  /** KEY-606: every release for this app, read ones included (newest first), for "View all updates". */
  loadAll?: () => Promise<(WhatsNewRelease & { dismissed?: boolean })[]>;
}) {
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const count = releases.length;
  // KEY-606: the user app only ever showed what you hadn't dismissed, so once you'd pressed "Got it" an update was
  // gone for good. The history is fetched fresh each time it is opened.
  const [history, setHistory] = useState<(WhatsNewRelease & { dismissed?: boolean })[] | null>(null);
  const [showingHistory, setShowingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  // The history is its own list, so flip the release there too (and put it back if the save fails).
  const setPublicEverywhere = onSetPublic
    ? async (key: string, isPublic: boolean) => {
        const flip = (value: boolean) => setHistory((h) => h && h.map((r) => (r.key === key ? { ...r, isPublic: value } : r)));
        flip(isPublic);
        try {
          await onSetPublic(key, isPublic);
        } catch (e) {
          flip(!isPublic);
          throw e;
        }
      }
    : undefined;

  async function openHistory() {
    if (!loadAll) return;
    setShowingHistory(true);
    setHistoryError(false);
    try {
      setHistory(await loadAll());
    } catch {
      setHistory([]);
      setHistoryError(true);
    }
  }

  if (showingHistory) {
    return (
      <Modal title="All updates" subtitle="Everything that has changed, newest first" onClose={() => onClose(dontShowAgain)} width={620}>
        {history === null ? (
          <p style={{ margin: "8px 0", fontSize: 14, color: "var(--text-muted)" }}>Loading…</p>
        ) : historyError ? (
          <p role="alert" style={{ margin: "8px 0", fontSize: 14, color: "var(--st-overdue)" }}>The history couldn't be loaded — try again in a moment.</p>
        ) : history.length === 0 ? (
          <p style={{ margin: "8px 0", fontSize: 14, color: "var(--text-muted)" }}>No updates have been published yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
            {history.map((release) => (
              <ReleaseSection key={release.key} release={release} seen={release.dismissed} onSetPublic={setPublicEverywhere} />
            ))}
          </div>
        )}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "var(--border-w) solid var(--border)", display: "flex", justifyContent: "space-between", gap: 12 }}>
          <button type="button" onClick={() => setShowingHistory(false)} className="kd-btn-dashed" style={{ width: "auto", padding: "8px 16px", fontSize: 13 }}>
            Back
          </button>
          <button onClick={() => onClose(dontShowAgain)} className="kd-btn-primary" style={{ padding: "0 22px", justifyContent: "center" }}>
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="What's New"
      subtitle={
        count === 0
          ? "You're all caught up"
          : count === 1
            ? "1 update you haven't seen yet"
            : `${count} updates you haven't seen yet`
      }
      onClose={() => onClose(dontShowAgain)}
      width={620}
    >
      {count === 0 ? (
        <p style={{ margin: "8px 0 4px", fontSize: 14, color: "var(--text-muted)" }}>
          There's nothing new right now — we'll let you know here when there is.
        </p>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
        {releases.map((release) => (
          <ReleaseSection key={release.key} release={release} onSetPublic={setPublicEverywhere} />
        ))}
      </div>
      )}

      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "var(--border-w) solid var(--border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {count > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>
              <input type="checkbox" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
              Don't show this again
            </label>
          )}
          {loadAll && (
            <button type="button" onClick={() => void openHistory()} className="kd-btn-dashed" style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}>
              View all updates
            </button>
          )}
        </div>
        <button onClick={() => onClose(dontShowAgain)} className="kd-btn-primary" style={{ padding: "0 22px", justifyContent: "center" }}>
          Got it
        </button>
      </div>
    </Modal>
  );
}
